import { differenceInDays, format, isBefore } from 'date-fns';
import { useMemo } from 'react';
import { MIN_ITEM_WIDTH } from './TimelineItemConstants';
import type { TimelineItemType } from './useTimelineTableColumns';
import { getNormalizePlannedDates } from './utils/timelineDateUtils';

export const useExpandedChildrenBackground = ({
  isExpanded,
  stackedChildren,
  timelineStartDate,
  timelineEndDate,
  intervalStart,
  dayWidth,
  leftOffset,
  calculatedWidth,
}: {
  isExpanded: boolean;
  stackedChildren: TimelineItemType[];
  timelineStartDate: string;
  timelineEndDate: string;
  intervalStart: Date;
  dayWidth: number;
  leftOffset: number;
  calculatedWidth: number;
}): { bgLeft: number; bgRight: number } => {
  return useMemo(() => {
    if (!isExpanded || stackedChildren.length === 0) {
      return { bgLeft: 0, bgRight: 0 };
    }
    let minLeft = 0;
    let maxRight = 0;
    for (const child of stackedChildren) {
      const childDates = getNormalizePlannedDates(
        child.plannedStartDateTime,
        child.plannedFinishDateTime,
      );
      if (!childDates.hasAnyDate) continue;
      const childNormStart = childDates.start as string;
      const childNormFinish = childDates.finish as string;
      const childStartDate = isBefore(childNormStart, timelineStartDate)
        ? format(timelineStartDate, 'yyyy-MM-dd')
        : format(childNormStart, 'yyyy-MM-dd');
      const childEndDate = isBefore(childNormFinish, timelineEndDate)
        ? format(childNormFinish, 'yyyy-MM-dd')
        : format(timelineEndDate, 'yyyy-MM-dd');
      const childRelativeLeft =
        differenceInDays(childStartDate, intervalStart) * dayWidth - leftOffset;
      const childNumDays = differenceInDays(childEndDate, childStartDate) + 1;
      const childWidth = Math.max(dayWidth * childNumDays, MIN_ITEM_WIDTH);
      minLeft = Math.min(minLeft, childRelativeLeft);
      maxRight = Math.max(maxRight, childRelativeLeft + childWidth - calculatedWidth);
    }
    return { bgLeft: minLeft, bgRight: -maxRight };
  }, [
    isExpanded,
    stackedChildren,
    timelineStartDate,
    timelineEndDate,
    intervalStart,
    dayWidth,
    leftOffset,
    calculatedWidth,
  ]);
};
